import { HyperliquidTradingFormVariant, OrderSide, OrderType, SwapDirection } from "@/components/features/perps/types";

// Hyperliquid requires $10 minimum for all orders. We use $11 (10% buffer) for price fluctuations.
export const HYPERLIQUID_MIN_ORDER_USD_WITH_BUFFER = 11;

export const HYPERLIQUID_DEFAULT_ORDER_TYPE: OrderType = "market";
export const HYPERLIQUID_DEFAULT_LEVERAGE = 10;

// Fallback cap used when `selectedMarket.maxLeverage` is momentarily unavailable
// (e.g., while the market list is loading). The same constant MUST be used by
// the preference-restore path AND the leverage modal so a restored preference
// can never exceed the cap the UI enforces.
export const HYPERLIQUID_MAX_LEVERAGE_FALLBACK = 50;
export const HYPERLIQUID_DEFAULT_FORM_VARIANT: HyperliquidTradingFormVariant = "panel";

export const HYPERLIQUID_FORM_CONTAINER_CLASS: Record<HyperliquidTradingFormVariant, string> = {
  panel: "space-y-5",
  drawer: "flex-1 overflow-y-auto px-6 space-y-5 pb-6",
};

export const HYPERLIQUID_ORDER_TYPE_CONFIG = [
  { type: "market" as OrderType, label: "Market" },
  { type: "limit" as OrderType, label: "Limit" },
] as const;

export const HYPERLIQUID_SUBMIT_BUTTON_CONFIG: Record<
  OrderSide,
  { label: string; variant: "primary" | "destructive" }
> = {
  long: { label: "Open Long", variant: "primary" },
  short: { label: "Open Short", variant: "destructive" },
};

export const HYPERLIQUID_VALIDATION_MESSAGES = {
  sizePositive: "Size must be greater than 0",
  limitPricePositive: "Limit price must be greater than 0",
  takeProfitPositive: "Take profit price must be greater than 0",
  stopLossPositive: "Stop loss price must be greater than 0",
} as const;

export const getHyperliquidPriceReferenceLabel = (orderType: OrderType) =>
  orderType === "limit" ? "entry price" : "current price";

export const getHyperliquidTpslValidationMessage = ({
  field,
  side,
  orderType,
}: {
  field: "takeProfit" | "stopLoss";
  side: OrderSide;
  orderType: OrderType;
}) => {
  const referenceLabel = getHyperliquidPriceReferenceLabel(orderType);

  if (field === "takeProfit") {
    return side === "long"
      ? `Take profit must be above ${referenceLabel} for longs`
      : `Take profit must be below ${referenceLabel} for shorts`;
  }

  return side === "long"
    ? `Stop loss must be below ${referenceLabel} for longs`
    : `Stop loss must be above ${referenceLabel} for shorts`;
};

export const HYPERLIQUID_BALANCE_LABELS = {
  prefix: "Perps:",
  currency: "USDC",
} as const;

export const HYPERLIQUID_INPUT_PLACEHOLDERS = {
  size: "0.00",
  takeProfit: "TP price",
  stopLoss: "SL price",
} as const;

export const HYPERLIQUID_FORM_COPY = {
  selectMarket: "Select a market to trade.",
  orderDetailsTitle: "Order details",
} as const;

export const HYPERLIQUID_ORDER_SUMMARY_FIELDS = [
  { key: "cost", label: "Order size" },
  { key: "fee", label: "Fees (est.)" },
  { key: "liquidationPrice", label: "Est. liq. price" },
  { key: "margin", label: "Required margin" },
  { key: "total", label: "Total cost", emphasize: true },
] as const;

export const HYPERLIQUID_DEFAULT_SWAP_DIRECTION: SwapDirection = "perp-to-spot";

export const HYPERLIQUID_SWAP_DIRECTIONS: SwapDirection[] = ["perp-to-spot", "spot-to-perp"];

export const HYPERLIQUID_SWAP_DIRECTION_LABELS: Record<
  SwapDirection,
  { title: string; subtitle: string; toPerp: boolean }
> = {
  "perp-to-spot": {
    title: "Perps → Spot",
    subtitle: "Top-up Spot balance",
    toPerp: false,
  },
  "spot-to-perp": {
    title: "Spot → Perps",
    subtitle: "Add trading margin",
    toPerp: true,
  },
};
